import { useState, createRef } from "react";
import { PieChart, Pie, ResponsiveContainer, Tooltip } from "recharts";
import PropTypes from "prop-types";

import { strCode } from "../../consts";
import { t } from "../../services";
import { useChartData } from "../../hooks";
import { useCustomTooltip } from "./hooks/useCustomTooltip";
import { useTransformedData } from "./hooks/useTransformedData";
import { useCalculateData } from "./hooks/useCalculateData";

import { Heading5 } from "../Text/Headings";
import GradientLoader from "../GradientLoader/GradientLoader";
import { Paragraph2 } from "../Text/Paragraphs";
import { RadialChartTooltip } from "./components/RadialChartTooltip";
import {
  ColumnLegendWrapper,
  NoInfo,
  RadialCartTotalTitle,
  RadialChartContainer,
  RadialChartWrapper
} from "./styled";
import ChartLegend from "./components/ChartLegend";

export const RadialChart = ({
  data = [],
  radius = 120,
  strokeWidth = 20,
  startAngle = 0,
  endAngle = 360,
  isLoading,
  metricsConfig,
  title,
  metricTotal,
  isColumnLegend,
  noDataMessage,
  showCalculatedTotal,
  showOriginalValue
}) => {
  const [activeIndex, setActiveIndex] = useState(null);

  const chartContainerRef = createRef();
  const { isTooltipHidden, tooltipPosition, showTooltip, hideTooltip } =
    useCustomTooltip(chartContainerRef);

  const { formattedData, filteredMetricsConfig, opacity, setOpacity } = useChartData({
    data: data,
    isBarChart: false,
    metricsConfig
  });

  const transformedData = useTransformedData(formattedData, filteredMetricsConfig, opacity);

  const metricTotalValue = metricTotal
    ? transformedData.find((item) => item.id === metricTotal)?.value
    : null;

  const totalValue = transformedData?.reduce((sum, segment) => sum + segment.value, 0);

  const calculatedData = useCalculateData(
    transformedData,
    activeIndex,
    totalValue,
    showOriginalValue
  );
  const isOneSegment = calculatedData?.filter((item) => item.value)?.length === 1;

  const formatTooltipValue = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const Legend = (
    <ChartLegend
      metricsConfig={filteredMetricsConfig}
      labelValuesConfig={calculatedData}
      opacity={opacity}
      setOpacity={setOpacity}
    />
  );

  return (
    <RadialChartContainer stroke={isOneSegment}>
      {!isLoading && !data?.length ? (
        <NoInfo height={`${radius * 2}px`}>
          <Paragraph2>{noDataMessage ? t(noDataMessage) : t(strCode.NO_DATA_FOR_CHART)}</Paragraph2>
        </NoInfo>
      ) : isLoading ? (
        <GradientLoader width={radius * 2} height={radius * 2} />
      ) : (
        <RadialChartWrapper
          ref={chartContainerRef}
          width={radius * 2}
          height={radius * 2}
          className="container"
          onMouseLeave={hideTooltip}
          onMouseMove={showTooltip}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                startAngle={startAngle}
                endAngle={endAngle}
                data={calculatedData}
                cx="50%"
                cy="50%"
                innerRadius={radius - strokeWidth}
                outerRadius={radius}
                labelLine={false}
                dataKey="value"
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              />

              <Tooltip
                wrapperStyle={{ zIndex: 1, display: isTooltipHidden ? "none" : "block" }}
                content={
                  <RadialChartTooltip
                    data={calculatedData[activeIndex]}
                    total={calculatedData[activeIndex]?.originalValue}
                  />
                }
                position={{ x: tooltipPosition.x, y: tooltipPosition.y }}
                formatter={formatTooltipValue}
              />
            </PieChart>
            <RadialCartTotalTitle>
              <Heading5>
                {metricTotal && !showCalculatedTotal ? metricTotalValue : totalValue}
              </Heading5>
              {t(title)}
            </RadialCartTotalTitle>
          </ResponsiveContainer>
        </RadialChartWrapper>
      )}

      {!isLoading && !data?.length ? null : isLoading ? (
        <GradientLoader height="100%" width={115} />
      ) : isColumnLegend ? (
        <ColumnLegendWrapper>{Legend}</ColumnLegendWrapper>
      ) : (
        Legend
      )}
    </RadialChartContainer>
  );
};

RadialChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      metrics: PropTypes.objectOf(PropTypes.number).isRequired
    })
  ).isRequired,
  title: PropTypes.string.isRequired,
  metricsConfig: PropTypes.shape(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  metricTotal: PropTypes.string,
  radius: PropTypes.number,
  strokeWidth: PropTypes.number,
  startAngle: PropTypes.number,
  endAngle: PropTypes.number,
  isColumnLegend: PropTypes.bool,
  noDataMessage: PropTypes.string,
  showCalculatedTotal: PropTypes.bool,
  showOriginalValue: PropTypes.bool
};
